import ContactLink from "@/components/ContactLink";
import { Link } from "@inertiajs/react";
import { BookOpen, Pencil, Undo2 } from "lucide-react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Button } from "@/components/ui/button";

function ActivityPreviewRow({ minutes, who, detail, note }) {
  return (
    <li className="flex items-start gap-3 py-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent">
        <BookOpen className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="text-sm">
          <p className="text-foreground"><span className="font-semibold text-accent">{minutes} min</span> · <span className="font-medium">{who}</span></p>
          {note && <p className="text-xs italic text-muted-foreground">{note}</p>}
          <p className="text-xs text-muted-foreground">{detail}</p>
        </div>
        <div className="-ml-3 flex gap-1">
          <Button variant="ghost" size="sm"><Pencil className="h-4 w-4" /> Edit</Button>
          <Button variant="ghost" size="sm"><Undo2 className="h-4 w-4" /> Undo</Button>
        </div>
      </div>
    </li>
  );
}

export default function UndoingMinuteBatches() {
  return (
    <>
      <p>
        Logged the wrong number, or realized someone wasn't there? Every reading log appears in your class's recent activity with <strong>Edit</strong> and <strong>Undo</strong> buttons.
      </p>

      <h2>Where to click</h2>
      <Screenshot caption="Recent activity on your classroom dashboard">
        <ul className="divide-y divide-border">
          <ActivityPreviewRow minutes={20} who="23 students" note="Charlotte's Web · ch 3" detail="2 hours ago · logged by a teacher" />
          <ActivityPreviewRow minutes={15} who="Sarah T." note="DEAR time" detail="5 hours ago · logged by a teacher" />
        </ul>
      </Screenshot>

      <p>
        <strong>Edit</strong> fixes the minutes (the classic 6 that should have been 60) for everyone in that log at once. <strong>Undo</strong> takes the whole log back: we ask you to confirm, saying exactly what's coming off, then totals drop right away and you can log the right number from your dashboard.
      </p>

      <h2>Who can fix what</h2>
      <p>
        Teachers can edit or undo any log for their own classroom, including one a family made at home. School admins can fix any log in the school. Families can edit or undo the logs they made themselves, but not the class's logs.
      </p>
      <p>
        Fixes are open until donations close (a week after reading ends, unless your school chose differently), even after new logs have closed.
      </p>

      <Callout variant="note" title="Prizes a student already earned stay earned">
        <p>
          If a student reached a prize because of the log you're undoing, they keep it. Taking a "you earned it!" moment back from a child isn't right. (If you need one cleared, email <ContactLink>hello@pagepledge.com</ContactLink> and we'll help.)
        </p>
      </Callout>

      <Callout variant="warning" title="There's no redo">
        <p>Once you undo a log, it's gone. If you change your mind, log it again.</p>
      </Callout>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/logging-classroom-minutes">Logging the whole class at once</Link>.</li>
        <li><Link href="/help/articles/logging-solo-minutes">Logging for one student</Link>.</li>
      </ul>
    </>
  );
}
